/**
 * /field/pf/jobs/:id — one of the hand's jobs (GET
 * /pipeline-facility/field/my-jobs/:id, fieldiq-api#1200). SNAKE_CASE.
 *
 * CREW ONLY: the API answers 403 not_on_crew to anyone else, whatever their
 * role, and this screen says so in a sentence rather than as a load failure.
 * Rent-ins come without vendor rates (the field surface never sends them).
 * The one action is today's log, on the tenant's today from `meta.today`.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { usePfFieldJob } from '../../../hooks/usePipelineFacility';
import { queryFailureError } from '../../../lib/load-failure';
import { fmtNumber, pfApiError, pfDay, pfJobStatusSkin, pfRefusal, pfRentStatusSkin } from '../../../lib/pipeline-facility';
import {
  PT_FIELD_PRIMARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
  PtRefusal,
} from '../../pressure-testing/field/PtFieldBits';
import { chainageRange } from '../JobsScreen';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
      <span className="text-sm text-fieldiq-muted">{label}</span>
      <span className="text-right text-sm font-semibold text-white">{value}</span>
    </div>
  );
}

/** A refusal that is the API's "not on this crew": a sentence, not a load failure. */
export function notOnCrewMessage(query: { error?: unknown; failureReason?: unknown }): string | null {
  const err = queryFailureError(query);
  return pfApiError(err).code === 'not_on_crew' ? pfRefusal(err, '') : null;
}

export default function PfFieldJobScreen() {
  const { id } = useParams<{ id: string }>();
  const query = usePfFieldJob(id);
  const job = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const chainageUnit = meta?.units?.chainage ?? null;
  const today = meta?.today ?? null;
  const refused = notOnCrewMessage(query);
  const loggable = job?.status === 'scheduled' || job?.status === 'in_progress';
  const todayLog = job?.today_log ?? null;

  return (
    <PtFieldPage title={job?.job_number || 'Job'} backTo="/field/pf" testId="pf-field-job">
      {refused ? (
        <PtRefusal message={refused} testId="pf-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={query} subject="this job">
          {!job ? (
            <PtRefusal message="That job no longer exists. It may have been deleted." />
          ) : (
            <div className="space-y-4">
              <PtFieldCard>
                <div className="flex items-center justify-between gap-2 border-b border-fieldiq-border px-4 py-3">
                  <span className="text-base font-bold text-white">{job.customer_name || '—'}</span>
                  <PtFieldBadge skin={pfJobStatusSkin(job.status)} />
                </div>
                <Row label="Site" value={job.site_name || '—'} />
                <Row label="Segment" value={job.segment_label || '—'} />
                <Row label={`Chainage${chainageUnit ? ` (${chainageUnit})` : ''}`} value={chainageRange(job.from_chainage, job.to_chainage, null)} />
                <Row label="Location" value={job.location_name || '—'} />
                <Row label="Supervisor" value={job.supervisor_name || '—'} />
                <Row label="Scheduled" value={`${pfDay(job.scheduled_start)} → ${pfDay(job.scheduled_end)}`} />
                {job.scope && <p className="px-4 py-3 text-sm text-fieldiq-text">{job.scope}</p>}
              </PtFieldCard>

              {loggable && today ? (
                <Link to={`/field/pf/jobs/${job.id}/log/${today}`} className={PT_FIELD_PRIMARY} data-testid="pf-field-today-log">
                  <ClipboardList className="size-5" aria-hidden="true" />
                  {todayLog ? `Today's log (${pfDay(today)})` : `Write today's log (${pfDay(today)})`}
                </Link>
              ) : (
                <p className="text-sm text-fieldiq-muted">{job.status === 'draft' ? 'This job is still a draft.' : 'This job takes no more logs.'}</p>
              )}
              {todayLog && (
                <p className="text-sm text-fieldiq-text" data-testid="pf-field-today-summary">
                  Today: {todayLog.crew.length} on crew hours, {todayLog.equipment.length} unit{todayLog.equipment.length === 1 ? '' : 's'}
                  {todayLog.updated_by_name ? ` · last saved by ${todayLog.updated_by_name}` : ''}
                </p>
              )}

              <section>
                <PtFieldSectionTitle>Crew · {job.crew.length}</PtFieldSectionTitle>
                <PtFieldCard>
                  {job.crew.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-fieldiq-muted">No crew.</p>
                  ) : (
                    job.crew.map((c) => <Row key={c.id} label={c.user_name || '—'} value={c.crew_role_label || c.crew_role || '—'} />)
                  )}
                </PtFieldCard>
              </section>

              <section>
                <PtFieldSectionTitle>Units · {job.equipment.length + job.rent_ins.length}</PtFieldSectionTitle>
                <PtFieldCard>
                  {job.equipment.length + job.rent_ins.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-fieldiq-muted">No units on this job.</p>
                  ) : (
                    <>
                      {job.equipment.map((e) => (
                        <Row key={e.id} label={e.equipment_name || '—'} value={e.unit_number || 'Owned'} />
                      ))}
                      {job.rent_ins.map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-3 border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
                          <span className="min-w-0 text-sm">
                            <span className="block truncate font-semibold text-white">{r.description || '—'}</span>
                            <span className="block truncate text-xs text-fieldiq-muted">
                              Rent-in · {r.vendor_name || '—'} · {pfDay(r.on_rent_on)} → {pfDay(r.off_rent_on)}
                            </span>
                          </span>
                          <PtFieldBadge skin={pfRentStatusSkin(r.rent_status)} />
                        </div>
                      ))}
                    </>
                  )}
                </PtFieldCard>
              </section>
              {todayLog && todayLog.equipment.some((e) => e.kind === 'owned' && e.hour_meter_end != null) && (
                <p className="text-xs text-fieldiq-muted">
                  Meters today:{' '}
                  {todayLog.equipment
                    .filter((e) => e.kind === 'owned' && e.hour_meter_end != null)
                    .map((e) => `${e.unit_name ?? '—'} ${fmtNumber(e.hour_meter_end)}`)
                    .join(' · ')}
                </p>
              )}
            </div>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
